import type {
  SecondaryPostEventsCpOrderPersistedTabContent,
  SecondaryQuotesCpOrderPersistedTabContent,
} from 'apiConnector/tabs/secondaryMarket.tabs.model';
import type { Custodian } from 'models/custodian';
import type { AppState } from 'state/state.model';
import * as fromSecondaryPostEventsWorkspace from 'state/workspaces/secondary-market/post-event/secondaryMarketPostEvent.selector';
import { fromBcsProductKey } from 'state/workspaces/secondary-market/quotes/secondartMarketQuotes.shared';
import * as fromSecondaryQuotesWorkspace from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';

import type { CpOrderSettlementType } from '../../referential/client/client.model';
import { getClientDefaultSettlementType } from '../../referential/client/client.selector';

function getInputFormData(appState: AppState, tabId: string) {
  return appState.secondaryMarket.cpOrder.form.input[tabId];
}

export function getInputFormQuantity(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.quantity;
}

export function getInputFormValuationDate(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.valuationDate;
}

export function getInputFormCashAmount(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.cashAmount;
}

export function getInputFormTradeSizeInputMode(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.tradeSizeInputMode;
}

export function getInputFormClientType(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.clientType;
}

export function getInputFormClient(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.client;
}

export function getInputFormSalesId(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.salesId;
}

export function getInputFormTradingPrice(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.tradingPrice;
}

export function getInputFormComment(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.comment;
}

export function getInputFormSalesMargin(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.salesMargin;
}

export function getInputFormSettlementDate(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.settlementDate;
}

export function getInputTradeDateType(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.tradeDateType;
}

export function getInputFormTradeWay(appState: AppState, tabId: string) {
  const form = getInputFormData(appState, tabId);
  return form?.tradeWay;
}

export function getInputFormSettlementType(appState: AppState, tabId: string): CpOrderSettlementType | undefined {
  const form = getInputFormData(appState, tabId);
  const defaultSettlementType = getClientDefaultSettlementType(appState, tabId, form?.tradeWay);
  return form?.settlementType ?? defaultSettlementType;
}

export function getInputFormCustodianBdrId(appState: AppState, tabId: string) {
  return getInputFormCustodian(appState, tabId)?.id;
}

export function getInputFormCustodian(appState: AppState, tabId: string): Custodian | undefined {
  const form = getInputFormData(appState, tabId);
  return form?.custodian;
}

export function getCpOrderTabContentToPersist(
  state: AppState,
  tabId: string,
): SecondaryQuotesCpOrderPersistedTabContent | undefined {
  const form = getInputFormData(state, tabId);
  if (form === undefined) {
    return undefined;
  }
  const bcskey = fromSecondaryQuotesWorkspace.getProductKey(state, tabId);

  return {
    ...form,
    bcsProductKey: bcskey,
    workspace: 'secondaryMarketQuotes',
    tabType: 'cpOrder',
  } as SecondaryQuotesCpOrderPersistedTabContent;
}

export function getAmendCpOrderTabContentToPersist(
  state: AppState,
  tabId: string,
): SecondaryPostEventsCpOrderPersistedTabContent | undefined {
  const form = getInputFormData(state, tabId);
  if (form === undefined) {
    return undefined;
  }
  const bcskey = fromBcsProductKey(fromSecondaryPostEventsWorkspace.getBcsProductKey(state, tabId));
  return {
    ...form,
    bcsProductKey: bcskey,
    workspace: 'secondaryMarketPostEvents',
    tabType: 'cpOrder',
  } as SecondaryPostEventsCpOrderPersistedTabContent;
}
